package diana.soleil.design.iraniancanadienbazzar.core.model

/**
 * Locale-aware number/price formatting, centralized so every screen renders digits the same way for
 * the active [Language]. Farsi uses Persian (Eastern Arabic) digits and the Persian decimal
 * separator; French uses a comma decimal and the trailing `$` (Canadian-French convention); English
 * uses Western digits and a leading `$`.
 *
 * Compose surfaces get the active language from `AppTheme.language`; pass it straight in. Numbers that
 * flow through a string resource with a `%1$d` placeholder must instead be pre-formatted here and
 * passed as `%1$s`, because Compose resource args always render Western digits.
 */

private const val PERSIAN_ZERO = '۰' // ۰

/** Map ASCII digits to the language's digit set (identity for EN/FR; Persian digits for FA). */
fun Language.localizeDigits(text: String): String {
    if (this != Language.FA) return text
    return buildString(text.length) {
        for (ch in text) {
            append(if (ch in '0'..'9') PERSIAN_ZERO + (ch - '0') else ch)
        }
    }
}

/** A whole-number count (likes, comments, elapsed units) with the active digit set. */
fun Language.formatCount(value: Long): String = localizeDigits(value.toString())

fun Language.formatCount(value: Int): String = formatCount(value.toLong())

/**
 * A CAD price from integer cents, localized: EN `$12.50`, FR `12,50 $`, FA `$۱۲٫۵۰`. Whole-dollar
 * amounts still show cents so prices line up in a list.
 */
fun Language.formatPriceCents(cents: Long): String {
    val dollars = cents / 100
    val remainder = (cents % 100).toString().padStart(2, '0')
    return when (this) {
        Language.FR -> "${localizeDigits(dollars.toString())},${localizeDigits(remainder)} $"
        Language.FA -> "$${localizeDigits(dollars.toString())}٫${localizeDigits(remainder)}"
        else -> "$$dollars.$remainder"
    }
}
